/**
 * PURPOSE: Co-ordinates post-bootstrap initialization.  This is needed because feature toggles and other data are not available
 *   when the app first loads.
 */
import type { AccessToken, IDToken, OktaAuthOptions, TokenManagerInterface, UserClaims } from '@okta/okta-auth-js';
import OktaAuth from '@okta/okta-auth-js';

// libraries
import * as bridgeUserSelectors from '@makemydeal/dr-dash-store/selectors/bridgeUserSelectors';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as creditDecisionSelectors from '@makemydeal/dr-dash-store/selectors/creditDecisionSelectors';
import * as dealLifecycleSelectors from '@makemydeal/dr-dash-store/selectors/dealLifecycleSelectors';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import { DMS_PLUS } from '@makemydeal/dr-dash-store/constants';
import * as multiScenarioActionCreators from '@makemydeal/dr-dash-store/actions/multiScenarioActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';
import * as vehicleActionCreators from '@makemydeal/dr-dash-store/actions/vehicleActionCreators';
import * as vehicleProtectionActionTypes from '@makemydeal/dr-dash-store/actionTypes/vehicleProtectionActionTypes';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as dealershipsActionCreators from '@makemydeal/dr-dash-store/actions/dealershipsActionCreators';
import * as dealValidationSelectors from '@makemydeal/dr-dash-store/selectors/dealValidationSelectors';
import { getCreditAppData } from '@makemydeal/dr-dash-store/actions/creditDecisionActionCreators';
import { fetchLenderList } from '@makemydeal/dr-dash-store/actions/lenderActionCreators';
import { generateShopperUrlsFromState } from '@makemydeal/dr-dash-store/actions/shopperUrlsActionCreators';
import type { BridgeUser } from '@makemydeal/dr-dash-types';
import { type AnyFSA } from '@makemydeal/dr-browser-utils';
import { setCustomAttribute } from '@makemydeal/dr-activities-common';
import type { DashMiddleware, DashNext } from '@makemydeal/dr-shared-store';
import {
    bootstrapActionTypes,
    bridgeUserActionTypes,
    dealerSelectors,
    featureToggleSelectors,
    initActionTypes
} from '@makemydeal/dr-shared-store';

// config
import type { CommonDataLayerV3Props } from '@makemydeal/dr-common-data-layer-utils';
import { initDrCdlV3 } from '@makemydeal/dr-common-data-layer-utils';
import {
    HostEnvironment,
    hostUrlHelper,
    locHrefUtil,
    MANAGER_VIEW_APP_PREFIX,
    publishKnownPendoIdsToNewRelic,
    publishPendoIdsFromSdkToNewRelic,
    sessionUtils
} from '@makemydeal/dr-shared-ui-utils';
import { DEAL_SCREEN_EXPERIENCE } from '../../../constants/constants';
import { DEFAULT_PENDO_ACCOUNT_ID } from '../../../constants';
import { fetchUserProfile } from '@makemydeal/dr-dash-store/actions/userProfileActionCreators';
import { syncDocumentsFromVIN } from '@makemydeal/dr-dash-store/actions/docSyncActionCreators';
import { ApplicationName } from '@makemydeal/dr-shared-types';
import { thunkOverrideUnsupportedDealType } from '@makemydeal/dr-dash-store/actions/deskingGlobalActionCreators';

const DOMAINS = ['@coxautoinc.com', '@coxinc.com'];

async function getUserEmail(authClient: OktaAuth): Promise<string | undefined> {
    try {
        const tokenManager: TokenManagerInterface = authClient.tokenManager;
        const accessToken: AccessToken = (await tokenManager.get('accessToken')) as AccessToken;
        const idToken: IDToken = (await tokenManager.get('idToken')) as IDToken;
        const userInfo: UserClaims = await authClient.token.getUserInfo(accessToken, idToken);

        return userInfo?.email;
    } catch (error) {
        console.error('Error getting user data:', error);
    }
    return undefined;
}

const initializePendo = (bridgeUser: BridgeUser, updateDxgIdNode: boolean) => {
    try {
        window.pendo.initialize({
            visitor: {
                id: bridgeUser.bridgeId,
                full_name: bridgeUser.fullName,
                ...(updateDxgIdNode && { dc_dealXgId: bridgeUser.dealXgId })
            },
            account: {
                id: bridgeUser.accountId ? `${bridgeUser.accountId}` : DEFAULT_PENDO_ACCOUNT_ID,
                dc_dealerName: bridgeUser.dealerName,
                dc_sessionId: sessionUtils.getAppSessionId(),
                ...(!updateDxgIdNode && { dc_dealXgId: bridgeUser.dealXgId }),
                dc_gtmVariation: bridgeUser.gtmVariation,
                dc_isTestDealer: bridgeUser.isTestDealer
            }
        });

        // Publish Pendo IDs to New Relic so every subsequent PageAction /
        // AjaxRequest / PageView / JavaScriptError inherits them as
        // custom attributes. This enables cross-tool session correlation
        // (Pendo ↔ New Relic) without a bespoke join. US2036660.
        //
        // Two calls, distinct attribute keys:
        //  - publishKnownPendoIdsToNewRelic: synchronous, publishes bridgeId /
        //    commonOrgId under `*FromBridge` keys so events emitted before
        //    the Pendo SDK finishes bootstrapping still carry a correlation
        //    key.
        //  - publishPendoIdsFromSdkToNewRelic: polls until the Pendo SDK is
        //    ready, then publishes Pendo's authoritative visitor/account/
        //    sessionIds and emits a PendoInitialized (or PendoInitFailed)
        //    PageAction with `matched: visitorId === bridgeId` for
        //    normalization-drift detection.
        const commonOrgId = bridgeUser.accountId ? `${bridgeUser.accountId}` : DEFAULT_PENDO_ACCOUNT_ID;
        publishKnownPendoIdsToNewRelic({ bridgeId: bridgeUser.bridgeId, commonOrgId });
        publishPendoIdsFromSdkToNewRelic({ bridgeId: bridgeUser.bridgeId, commonOrgId });
    } catch (error) {
        console.error('Failed to initialize Pendo:', error);
    }
};

export const postBootstrapMiddleware: DashMiddleware = (store: any) => (next: DashNext) => async (action: AnyFSA) => {
    // need reducers to execute first so that we can use the state that's updated by INIT_PENCIL_SUCCESS
    const nextResult = next(action);
    let state;
    const config: OktaAuthOptions = {
        issuer: 'https://authorize.coxautoinc.com/oauth2/aus132uaxy2eomhmi357'
    };

    switch (action.type) {
        case bridgeUserActionTypes.BRIDGE_USER_SUCCESS: {
            state = store.getState();

            const isPendoEnabled = featureToggleSelectors.enablePendoInMV(state);

            if (isPendoEnabled) {
                const bridgeId = bridgeUserSelectors.getBridgeId(state);
                const fullName = bridgeUserSelectors.getFullName(state);
                const accountId = offerReduxSelectors.getCommonOrgId(state);
                const updateDxgIdNode = featureToggleSelectors.isEnableUpdatePendoMetadata(state);

                let pendoObj: BridgeUser = { bridgeId, fullName };
                pendoObj = { ...pendoObj, accountId };
                pendoObj = {
                    ...pendoObj,
                    dealerName: dealerSelectors.getDealerName(state),
                    dealXgId: offerReduxSelectors.getDealXgId(state),
                    gtmVariation: dealXgDetailsSelectors.getIsEcommerceDeal(state)
                        ? 'Ecommerce'
                        : dealerSelectors.getDMSIntegrationToggle(state) === DMS_PLUS
                        ? DMS_PLUS
                        : DEAL_SCREEN_EXPERIENCE.DEAL_CENTRAL,
                    isTestDealer: dealerSelectors.getIsTestDealer(state)
                };
                initializePendo(pendoObj, updateDxgIdNode);
            }

            const environmentEnum = hostUrlHelper.getHostUrlEnvironment(MANAGER_VIEW_APP_PREFIX, locHrefUtil.getLocHref());
            let isUserInternal = undefined;
            try {
                const authClient: OktaAuth = new OktaAuth(config);
                const userEmail = await getUserEmail(authClient);
                if (userEmail) {
                    isUserInternal = DOMAINS.some((domain) => domain.includes(userEmail.split('@')[1])) || undefined;
                }
                const currentOfferType = offerReduxSelectors.getCurrentOfferType(state);
                const cdlV3Data: CommonDataLayerV3Props = {
                    application: {
                        componentId: 'CI2355894',
                        isProduction: environmentEnum === HostEnvironment.Prod,
                        name: 'Manager View',
                        workloadId: 'CI2355893'
                    },
                    client: {
                        name: dealerSelectors.getDealerName(state),
                        commonOrgId: offerReduxSelectors.getCommonOrgId(state),
                        isTestDealer: dealerSelectors.getIsTestDealer(state),
                        vinsolutionsId: dealerSelectors.getVinSolutionsDealerId(state)
                    },
                    user: {
                        bridgePlatformId: bridgeUserSelectors.getBridgeId(state),
                        bridgeUsername: bridgeUserSelectors.getBridgeUsername(state),
                        isInternalUser: isUserInternal,
                        type: 'dealer'
                    },
                    vehicle: {
                        vin: vehicleSelectors.getVehicleVin(state),
                        make: vehicleSelectors.getVehicleMake(state),
                        model: vehicleSelectors.getVehicleModel(state),
                        year: vehicleSelectors.getVehicleYear(state)
                    },
                    deal: {
                        dealId: offerReduxSelectors.getDealXgId(state),
                        dealVersion: offerReduxSelectors.getDealXgVersion(state),
                        dealStatus: dealLifecycleSelectors.getDealLifecycleStatus(state),
                        offerPrice: offerReduxSelectors.getOfferPrice(state),
                        downPayment: offerReduxSelectors.getDownPaymentByOfferType(state, currentOfferType),
                        termLength: offerReduxSelectors.getTermIdByOfferType(state, currentOfferType),
                        lenderDecision: creditDecisionSelectors.getLenderCreditDecisionsByOfferType(state, currentOfferType),
                        monthlyPayment: offerReduxSelectors.getBaseMonthlyPaymentTotal(state),
                        paymentFrequency: offerSelectors.getPaymentFrequency(state),
                        sellRate: offerReduxSelectors.getSellRate(state),
                        buyRate: offerReduxSelectors.getBuyRate(state),
                        residualValue: offerReduxSelectors.getBaseResidualDollar(state),
                        acquisitionFee: offerReduxSelectors.getAcquisitionFeeFromTerm(state),
                        maxResidualMSRP: offerReduxSelectors.getMaxResidualizedMsrp(state),
                        securityDeposit: offerReduxSelectors.getSecurityDepositOverride(state, true),
                        commonConsumerId: offerReduxSelectors.getShopperCommonConsumerId(state),
                        globalCustomerId: dealXgDetailsSelectors.getGlobalCustomerId(state),
                        incentivesValue: offerReduxSelectors.getAllIncentiveTotal(state),
                        incentives: offerReduxSelectors.getAppliedIncentives(state),
                        tradeValue: tradeInSelectors.getTradeValue(state),
                        trade: tradeInSelectors.getTradeIn(state),
                        paymentMethod: currentOfferType,
                        autoLeadId: dealXgDetailsSelectors.getAutoLeadId(state),
                        leaseMileage: offerReduxSelectors.getLeaseMileage(state)?.toString()
                    }
                };
                initDrCdlV3(cdlV3Data);
                store.dispatch({ type: 'CDLV3_DATA_READY' });
            } catch (error) {
                console.error('Could not intialize initDrCdlV3', error);
            }

            const bridgeId = bridgeUserSelectors.getBridgeId(state);
            const { enableRoleAssumptionInMV, mvDealValidation } = featureToggleSelectors.getFeatureToggles(state);
            const isEmployee = dealValidationSelectors.getIsEmployee(state);
            const isDealValid = dealValidationSelectors.isDealValid(state);

            const skipDealerShipWhenDealIsInvalid = enableRoleAssumptionInMV && isEmployee && !isDealValid && mvDealValidation;
            // Only fetch dealerships if NOT in role assumption mode with an invalid deal and user is a Cox internal employee.
            // This prevents dealership fetch during role assumption when the deal is invalid (DE715475).
            if (bridgeId && !skipDealerShipWhenDealIsInvalid) {
                store.dispatch(dealershipsActionCreators.fetchDealerships(bridgeId));
            }

            break;
        }
        case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
            state = store.getState();
            store.dispatch({ type: vehicleProtectionActionTypes.DASH_MENU_SKIPPED });

            store.dispatch(fetchUserProfile());

            if (offerInfoSelectors.getIsMakingCurrent(state)) {
                store.dispatch(multiScenarioActionCreators.handleFetchMakeCurrentMultipleScenarios());
                break;
            }

            store.dispatch(multiScenarioActionCreators.handleFetchMultipleScenarios());

            // Override unsupported deal types to Finance
            store.dispatch(thunkOverrideUnsupportedDealType());

            break;
        }
        case initActionTypes.INIT_SUCCESS: {
            state = store.getState();
            const dealerId = dealerSelectors.getDealerId(state);
            const dealXgId = offerReduxSelectors.getDealXgId(state);
            const dealXgVersion = offerInfoSelectors.getDealXgVersion(state);
            const correlationId = offerReduxSelectors.getUdCorrelationId(state);
            setCustomAttribute('customattr.coxauto:mmdDealerId', `${dealerId}`);
            setCustomAttribute('customattr.coxauto:dealXgId', `${dealXgId}`);
            setCustomAttribute('customattr.coxauto:dealXgVersion', `${dealXgVersion}`);
            setCustomAttribute('customattr.coxauto:correlationId', `${correlationId}`);
            setCustomAttribute('customattr.coxauto:paymentsCalculated', action.payload?.meta?.paymentsCalculated === 'true');

            const creditAppId = offerReduxSelectors.getCreditAppId(state);

            if (creditAppId) {
                store.dispatch(getCreditAppData(creditAppId, dealerId));
            }

            store.dispatch(fetchLenderList(state));

            const orgId = offerReduxSelectors.getCommonOrgId(state);

            if (!orgId) return;

            const vin = vehicleSelectors.getVehicleVin(state);
            const stockNumber = vehicleSelectors.getVehicleStockNumber(state);

            const query = vin || stockNumber;

            if (query) {
                const bffEndpoint = configSelectors.getServicesBff(state);
                store.dispatch(vehicleActionCreators.fetchCviVehiclesResults(query, orgId, bffEndpoint));
            }

            // Document sync is unconditional: every deal load with a dealXgId syncs all
            // registry-configured doc types via the secure docSyncFromVIN lambda.
            if (dealXgId) {
                store.dispatch(syncDocumentsFromVIN(dealXgId, ApplicationName.CMD, dealerId));
            }
            break;
        }
        case initActionTypes.INIT_PENCIL_SUCCESS: {
            state = store.getState();
            const featureToggles = featureToggleSelectors.getFeatureToggles(state);

            // Generate shopper URLs if deferred generation toggle is enabled
            if (featureToggles.enableDeferredShopperUrlGeneration) {
                store.dispatch(generateShopperUrlsFromState());
            }

            break;
        }
    }

    return nextResult;
};
